import uuid from 'uuid-random';

import {isShallowEqual} from '../../../helpers/reselectHelpers';
import {AppAction} from '../../appActions';
import {
  CellsPanelConversationsActionsEnum,
  CellsPanelMessagesActionsEnum,
} from '../../panels/cells/cellsPanelActions';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {InboxesParameters} from '../../panels/inboxes/parameters/inboxesParameters';
import {emptyInboxesSelection} from '../../panels/inboxes/parameters/inboxesParametersHelpers';
import {cellsListReducer} from './cellsList/cellsListReducer';
import {CellsListState, cellsListTypeForParameters, CellsSyncContext} from './cellsList/cellsListState';
import {CellsListsActionsEnum} from './cellsListsActions';
import {isCellsListWritableForParameters, makeCellsSyncContext} from './cellsListsHelpers';
import {CellsListsState} from './cellsListsState';

/*
 * Reducer.
 */

export function cellsListsReducer(state: CellsListsState = [], action: AppAction): CellsListsState {
  switch (action.type) {
    case CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_COMPLETED:
    case CellsPanelMessagesActionsEnum.FETCH_MESSAGES_COMPLETED:
    case CellsListsActionsEnum.PREFETCH_CELLS_COMPLETED: {
      const {cellsContext, parameters} = action.payload;
      const cellsSyncContext = makeCellsSyncContext(cellsContext);
      if (!cellsSyncContext) {
        return state;
      }

      // If we don't have a cache entry for these parameters, add a new one.
      if (!state.some((list) => isCellsListWritableForParameters(list, parameters))) {
        const list = makeCellsList(cellsSyncContext, cellsContext, parameters);
        return [...state, ...defaultCellsListsReducer([list], action)];
      }

      return defaultCellsListsReducer(state, action);
    }

    default:
      return defaultCellsListsReducer(state, action);
  }
}

function defaultCellsListsReducer(prevState: CellsListsState, action: AppAction): CellsListsState {
  const nextState = prevState
    .map((list) => cellsListReducer(list, action))
    .filter((list) => list.ranges.length > 0);

  if (isShallowEqual(prevState, nextState)) {
    return prevState;
  }

  return nextState;
}

/*
 * Helpers.
 */

function makeCellsList<T extends InboxesParameters>(
  cellsSyncContext: CellsSyncContext,
  cellsContext: CellsContext,
  parameters: T,
): CellsListState<T> {
  return {
    uid: uuid(),
    type: cellsListTypeForParameters(parameters),
    cellsSyncContext,
    cellsContext,
    parameters: {
      ...parameters,
      selection: emptyInboxesSelection,
      activity: undefined,
    },
    ranges: [],
  };
}
